import { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { RootState } from '../../../../store/root-reducer';
import {
  LeadFinderSecondaryTab,
  LeadRevealType,
} from '../../types';
import {
  SAVED_TAB_BULK_OPERATION_LIMIT,
} from '../../helpers/helper';
import { leadFinderBulkOperationLimit ,
  getUserTrackingId,
  getUserShAccountId,
  getUserPlanName,
  getUserRole,
  getUserLeadFinderPlanName,
} from '../../../../shared/utils/user-details';
import {
  openCreditNotAvailableModal,
  setShowBulkRevealTagModal,
  setShowSelectedMoreThanLimitModal,
} from '../../v2-lead-slice';

import { AnalyticsEvents } from '../../../../shared/enums/analytics';

interface UseBulkRevealProps {
  handleBulkReveal: (revealType: LeadRevealType, tagIds: number[], newTags: string[]) => Promise<void>;
  leadsData: { profiles: any[]; pagination: any };
  leadCredits: number;
  activeSubTab: LeadFinderSecondaryTab;
}

export const useBulkReveal = (props: UseBulkRevealProps) => {
  const { handleBulkReveal, leadsData, leadCredits, activeSubTab } = props;

  const dispatch = useDispatch();
  const { isSelectedAllLeads, selectedLeads } = useSelector(
    (state: RootState) => state.leadFinderV2.selection,
  );

  const [isBulkRevealing, setIsBulkRevealing] = useState(false);
  const [tagsToAdd, setTagsToAdd] = useState<any[]>([]);
  const [leadRevealType, setLeadRevealType] = useState<LeadRevealType | null>(null);

  const leadFinderBulkOperationLimitValue = leadFinderBulkOperationLimit();

  // Compute topPeopleLimit inline from leadCredits + leadFinderBulkOperationLimit
  const topPeopleLimit =
    Number(leadCredits) >= leadFinderBulkOperationLimitValue
      ? leadFinderBulkOperationLimitValue
      : Number(leadCredits);

  // Handle bulk reveal with tag modal
  const handleBulkRevealWithTags = useCallback(
    (revealType: LeadRevealType) => {
      // Credit checks for bulk reveal
      if (topPeopleLimit <= 0) {
        dispatch(openCreditNotAvailableModal(revealType));
        return;
      }

      // Calculate count
      let count = 0;
      if (isSelectedAllLeads) {
        count =
          leadsData?.pagination?.totalRows > leadFinderBulkOperationLimitValue
            ? leadFinderBulkOperationLimitValue
            : leadsData?.pagination?.totalRows || 0;
      } else {
        // Count selected leads that are not revealed (or re-reveal for EMAIL_PHONE)
        const selectedLeadsData = leadsData?.profiles?.filter((lead) =>
          selectedLeads.includes(lead.id),
        );
        count =
          selectedLeadsData?.filter(
            (l) =>
              !l?.isRevealed ||
              (revealType === LeadRevealType.EMAIL_PHONE &&
                l?.reReveal &&
                l?.teaser?.phones?.length > 0),
          )?.length || 0;
      }

      // Check if count exceeds bulk operation limit
      const bulkOperationLimit =
        activeSubTab === LeadFinderSecondaryTab.SAVED
          ? SAVED_TAB_BULK_OPERATION_LIMIT
          : leadFinderBulkOperationLimit();
      if (count > bulkOperationLimit) {
        dispatch(setShowSelectedMoreThanLimitModal(true));
        return;
      }

      // Check credits
      if (
        (revealType === LeadRevealType.EMAIL && Number(leadCredits) < count) ||
        (revealType === LeadRevealType.EMAIL_PHONE &&
          Number(leadCredits) < count * 7)
      ) {
        dispatch(openCreditNotAvailableModal(revealType));
        // Track analytics
        window.analytics?.track?.({
          userId: getUserTrackingId(),
          event: AnalyticsEvents.TrippedFeature,
          properties: {
            shAccountId: getUserShAccountId(),
            current_plan: getUserPlanName(),
            role: getUserRole(),
            'LF Plan': getUserLeadFinderPlanName(),
            feature_type: 'Lead Finder',
          },
        });
        return;
      }

      // All checks passed, show tag modal
      setLeadRevealType(revealType);
      dispatch(setShowBulkRevealTagModal(true));
    },
    [
      topPeopleLimit,
      isSelectedAllLeads,
      leadsData,
      selectedLeads,
      leadCredits,
      activeSubTab,
      dispatch,
      leadFinderBulkOperationLimitValue,
    ],
  );

  // Handle bulk reveal submit (from modal)
  const handleBulkRevealSubmit = useCallback(async () => {
    if (!leadRevealType) return;

    setIsBulkRevealing(true);
    const tagIds: number[] = [];
    const newTags: string[] = [];

    tagsToAdd.forEach((tag) => {
      if (tag.isNew) {
        newTags.push(tag.name);
      } else {
        tagIds.push(tag.id);
      }
    });

    await handleBulkReveal(leadRevealType, tagIds, newTags);
    setIsBulkRevealing(false);
    dispatch(setShowBulkRevealTagModal(false));
    setTagsToAdd([]);
    setLeadRevealType(null);
  }, [
    leadRevealType,
    tagsToAdd,
    handleBulkReveal,
    dispatch,
  ]);

  return {
    isBulkRevealing,
    tagsToAdd,
    setTagsToAdd,
    leadRevealType,
    setLeadRevealType,
    handleBulkRevealWithTags,
    handleBulkRevealSubmit,
  };
};
